import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Users, Plus, Search, Edit2, Trash2, CheckCircle, XCircle, 
  Phone, Mail, Calendar, DollarSign, Package, RefreshCw, X,
  AlertTriangle, Gift, UserPlus, User, ChevronDown, ChevronUp,
  FileText, Clock, Building2, ExternalLink, Eye, Save, Hash,
  History, Loader2, MapPin, Briefcase, Shield, CreditCard, Filter, Key
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Import safeFetch from shared utility
import { safeFetch } from '../../utils/safeFetch';

// Client type colors
const CLIENT_TYPE_CONFIG = {
  subscription: { label: 'Subscription', bg: 'bg-[#0D9488]', text: 'text-white', badge: 'bg-teal-100 text-teal-700' },
  one_time: { label: 'One-Time', bg: 'bg-[#64748B]', text: 'text-white', badge: 'bg-gray-100 text-gray-700' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', badge: 'bg-green-100 text-green-700' },
  inactive: { label: 'Inactive', badge: 'bg-red-100 text-red-700' },
};

const AdminClients = () => {
  // Main state
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'subscription', 'one_time', 'registrations'
  const [clients, setClients] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  // Client Detail Drawer State
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [clientActivityHistory, setClientActivityHistory] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Add/Gift modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftingClient, setGiftingClient] = useState(null);
  const [giftAmount, setGiftAmount] = useState(1);
  const [giftReason, setGiftReason] = useState('');
  const [isGifting, setIsGifting] = useState(false);
  
  // Legal Terms Modal
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [viewingTermsClient, setViewingTermsClient] = useState(null);

  // Authorized users
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addingUserToClient, setAddingUserToClient] = useState(null);
  const [newUserData, setNewUserData] = useState({ name: '', mobile: '', email: '', role: 'installer' });
  const [isAddingUser, setIsAddingUser] = useState(false);

  // Merge modal
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergingRegistration, setMergingRegistration] = useState(null);
  const [mergeTargetMobile, setMergeTargetMobile] = useState('');
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState('');

  // Set Password modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordClient, setPasswordClient] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // New client form
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    mobile: '',
    email: '',
    abn: '',
    client_type: 'subscription',
    bookings_per_week: 5,
    weekly_cost: 45,
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    fetchAllClients();
    fetchRegistrations();
  }, []);

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
  });

  const fetchAllClients = async () => {
    setLoading(true);
    try {
      // Fetch both regular clients and subscription clients using safeFetch (bypasses platform fetch interception)
      const [regularResult, subscriptionResult] = await Promise.all([
        safeFetch(`${API_URL}/api/clients/`, { headers: getAuthHeader() }),
        safeFetch(`${API_URL}/api/subscription-clients/`, { headers: getAuthHeader() })
      ]);

      let allClients = [];

      if (regularResult.ok && Array.isArray(regularResult.data)) {
        const regularClients = regularResult.data;
        // Mark regular clients as one_time type
        allClients = regularClients.map(c => ({
          ...c,
          client_type: c.subscription_package === 'subscription' ? 'subscription' : 'one_time',
          _source: 'clients'
        }));
      }

      if (subscriptionResult.ok && Array.isArray(subscriptionResult.data)) {
        const subscriptionClients = subscriptionResult.data;
        // Mark subscription clients and add to list (avoiding duplicates by mobile)
        const existingMobiles = new Set(allClients.map(c => c.mobile));
        const newSubClients = subscriptionClients
          .filter(c => !existingMobiles.has(c.mobile))
          .map(c => ({
            ...c,
            client_type: 'subscription',
            _source: 'subscription_clients'
          }));
        allClients = [...allClients, ...newSubClients];
      }

      // Sort by name
      allClients.sort((a, b) => (a.name || a.business_name || '').localeCompare(b.name || b.business_name || ''));
      setClients(allClients);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    try {
      const result = await safeFetch(`${API_URL}/api/subscription-signups/`, {
        headers: getAuthHeader()
      });
      if (result.ok && Array.isArray(result.data)) {
        setRegistrations(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch registrations:', err);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Are you sure you want to delete ${client.name || client.business_name}?`)) return;

    try {
      const endpoint = client._source === 'subscription_clients' 
        ? `${API_URL}/api/subscription-clients/${client.id}`
        : `${API_URL}/api/clients/${client.id}`;
      
      const result = await safeFetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (result.ok) {
        fetchAllClients();
      } else {
        alert('Failed to delete client');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete client');
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const endpoint = formData.client_type === 'subscription'
        ? `${API_URL}/api/subscription-clients/`
        : `${API_URL}/api/clients/`;

      const payload = formData.client_type === 'subscription' 
        ? {
            name: formData.name,
            business_name: formData.business_name,
            mobile: formData.mobile,
            email: formData.email,
            abn: formData.abn,
            bookings_per_week: formData.bookings_per_week,
            weekly_cost: formData.weekly_cost,
            status: formData.status,
            notes: formData.notes
          }
        : {
            name: formData.name,
            business_name: formData.business_name,
            mobile: formData.mobile,
            email: formData.email,
            abn: formData.abn,
            subscription_package: 'none',
            notes: formData.notes
          };

      const result = await safeFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(payload)
      });

      if (result.ok) {
        setShowAddModal(false);
        setFormData({
          name: '', business_name: '', mobile: '', email: '', abn: '',
          client_type: 'subscription', bookings_per_week: 5, weekly_cost: 45,
          status: 'active', notes: ''
        });
        fetchAllClients();
      } else {
        setError(result.data?.detail || 'Failed to add client');
      }
    } catch (err) {
      setError('Failed to add client');
    }
  };

  // Client Detail Drawer Functions
  const openClientDetail = async (client) => {
    setSelectedClient(client);
    setIsEditMode(false);
    setEditForm({});
    await fetchClientActivityHistory(client.mobile);
  };

  const closeClientDetail = () => {
    setSelectedClient(null);
    setIsEditMode(false);
    setEditForm({});
    setClientActivityHistory([]);
  };

  const startEditMode = () => {
    setEditForm({
      name: selectedClient.name || '',
      business_name: selectedClient.business_name || '',
      mobile: selectedClient.mobile || '',
      email: selectedClient.email || '',
      abn: selectedClient.abn || '',
      bookings_per_week: selectedClient.bookings_per_week || 5,
      weekly_cost: selectedClient.weekly_cost || 45,
      status: selectedClient.status || 'active',
      notes: selectedClient.notes || '',
      accounts_contact: selectedClient.accounts_contact || {
        same_as_key_contact: true,
        first_name: '', last_name: '', email: '', phone: ''
      }
    });
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    setIsEditMode(false);
    setEditForm({});
  };

  const saveClientEdit = async () => {
    setIsSaving(true);
    try {
      const endpoint = selectedClient._source === 'subscription_clients'
        ? `${API_URL}/api/subscription-clients/${selectedClient.id}`
        : `${API_URL}/api/clients/${selectedClient.id}`;

      const result = await safeFetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(editForm)
      });

      if (result.ok) {
        setSelectedClient({ ...selectedClient, ...editForm });
        setIsEditMode(false);
        fetchAllClients();
        alert('Client updated successfully!');
      } else {
        alert(result.data?.detail || 'Failed to update client');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchClientActivityHistory = async (mobile) => {
    setLoadingActivity(true);
    try {
      const result = await safeFetch(`${API_URL}/api/activity/?phone=${mobile}&limit=50`, {
        headers: getAuthHeader()
      });
      if (result.ok) {
        setClientActivityHistory(result.data?.activities || []);
      }
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Gift bookings (for subscription clients)
  const openGiftModal = (client) => {
    setGiftingClient(client);
    setGiftAmount(1);
    setGiftReason('');
    setShowGiftModal(true);
  };

  const handleGiftBookings = async () => {
    if (!giftingClient || giftAmount < 1) return;
    setIsGifting(true);

    try {
      const result = await safeFetch(`${API_URL}/api/subscription-clients/${giftingClient.id}/adjust-quota`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          adjustment_type: 'gift',
          amount: giftAmount,
          reason: giftReason || 'Gift bookings'
        })
      });

      if (result.ok) {
        setShowGiftModal(false);
        setGiftingClient(null);
        fetchAllClients();
        alert(`Successfully gifted ${giftAmount} booking(s)`);
      } else {
        alert(result.data?.detail || 'Failed to gift bookings');
      }
    } catch (err) {
      alert('Failed to gift bookings');
    } finally {
      setIsGifting(false);
    }
  };

  // View Terms
  const viewClientTerms = (client) => {
    setViewingTermsClient(client);
    setShowTermsModal(true);
  };

  // Authorized Users
  const openAddUserModal = (client) => {
    setAddingUserToClient(client);
    setNewUserData({ name: '', mobile: '', email: '', role: 'installer' });
    setShowAddUserModal(true);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addingUserToClient) return;
    setIsAddingUser(true);

    try {
      const result = await safeFetch(`${API_URL}/api/subscription-clients/${addingUserToClient.id}/authorized-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(newUserData)
      });

      if (result.ok) {
        setShowAddUserModal(false);
        setAddingUserToClient(null);
        fetchAllClients();
        alert('User added successfully');
      } else {
        alert(result.data?.detail || 'Failed to add user');
      }
    } catch (err) {
      alert('Failed to add user');
    } finally {
      setIsAddingUser(false);
    }
  };

  // Registration management
  const updateRegistrationStatus = async (signupId, newStatus) => {
    try {
      const result = await safeFetch(`${API_URL}/api/subscription-signups/${signupId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (result.ok) {
        fetchRegistrations();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const openMergeModal = (registration) => {
    setMergingRegistration(registration);
    setMergeTargetMobile(registration.key_contact?.mobile || '');
    setMergeError('');
    setShowMergeModal(true);
  };

  const handleMergeRegistration = async () => {
    if (!mergingRegistration || !mergeTargetMobile) return;
    setIsMerging(true);
    setMergeError('');

    try {
      const result = await safeFetch(
        `${API_URL}/api/subscription-signups/${mergingRegistration.id}/merge-with-client/${mergeTargetMobile}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        }
      );

      if (result.ok) {
        setShowMergeModal(false);
        setMergingRegistration(null);
        fetchRegistrations();
        fetchAllClients();
        alert('Successfully merged registration with existing client!');
      } else {
        setMergeError(result.data?.detail || 'Failed to merge registration');
      }
    } catch (err) {
      setMergeError('Network error. Please try again.');
    } finally {
      setIsMerging(false);
    }
  };

  // Set Password for subscription client
  const openPasswordModal = (client) => {
    setPasswordClient(client);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handleSetPassword = async () => {
    if (!passwordClient) return;
    
    if (!newPassword || newPassword.length < 4) {
      setPasswordError('Password must be at least 4 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsSettingPassword(true);
    setPasswordError('');

    try {
      const result = await safeFetch(
        `${API_URL}/api/client-portal/admin-set-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            mobile: passwordClient.mobile,
            password: newPassword
          })
        }
      );

      if (result.ok) {
        setShowPasswordModal(false);
        setPasswordClient(null);
        alert(`Password set successfully for ${passwordClient.name || passwordClient.mobile}!`);
      } else {
        setPasswordError(result.data?.detail || 'Failed to set password');
      }
    } catch (err) {
      setPasswordError('Network error. Please try again.');
    } finally {
      setIsSettingPassword(false);
    }
  };

  const formatActivityDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Australia/Melbourne'
    });
  };

  // Filter clients based on tab and search
  const filteredClients = clients.filter(client => {
    const matchesSearch = !searchTerm || 
      (client.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.business_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.mobile || '').includes(searchTerm) ||
      (client.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTab = activeTab === 'all' || client.client_type === activeTab;

    return matchesSearch && matchesTab;
  });

  // Stats
  const stats = {
    total: clients.length,
    subscription: clients.filter(c => c.client_type === 'subscription').length,
    oneTime: clients.filter(c => c.client_type === 'one_time').length,
    active: clients.filter(c => c.status === 'active').length,
    pendingRegistrations: registrations.filter(r => r.status === 'pending').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Clients</h1>
          <p className="text-[#64748B]">Manage all your clients in one place</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-[#0D9488] hover:bg-[#0F766E]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#F1F5F9]">
                <Users className="w-5 h-5 text-[#64748B]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111111]">{stats.total}</p>
                <p className="text-xs text-[#64748B]">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#F0FDFA]">
                <CreditCard className="w-5 h-5 text-[#0D9488]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0D9488]">{stats.subscription}</p>
                <p className="text-xs text-[#64748B]">Subscription</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#F1F5F9]">
                <Package className="w-5 h-5 text-[#64748B]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#64748B]">{stats.oneTime}</p>
                <p className="text-xs text-[#64748B]">One-Time</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                <p className="text-xs text-[#64748B]">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.pendingRegistrations}</p>
                <p className="text-xs text-[#64748B]">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-[#E2E8F0]">
        {[
          { id: 'all', label: `All (${stats.total})` },
          { id: 'subscription', label: `Subscription (${stats.subscription})` },
          { id: 'one_time', label: `One-Time (${stats.oneTime})` },
          { id: 'registrations', label: `Registrations (${registrations.length})` },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-[#0D9488] text-[#0D9488]'
                : 'border-transparent text-[#64748B] hover:text-[#111111]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      {activeTab !== 'registrations' && (
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              placeholder="Search by name, mobile, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      )}

      {/* Clients List */}
      {activeTab !== 'registrations' ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-0">
            {filteredClients.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto text-[#94A3B8] mb-4" />
                <h3 className="text-lg font-medium text-[#111111] mb-2">No clients found</h3>
                <p className="text-[#64748B]">
                  {searchTerm ? 'Try adjusting your search' : 'Add your first client to get started'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Details</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-[#64748B] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {filteredClients.map((client) => (
                      <tr key={client.id || client.mobile} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-[#111111]">{client.name || client.business_name}</p>
                            <p className="text-xs text-[#64748B]">
                              {client.client_ref || `${client.client_type === 'subscription' ? 'SUB' : 'CLT'}-${(client.id || '').slice(0, 8).toUpperCase()}`}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            CLIENT_TYPE_CONFIG[client.client_type]?.badge || 'bg-gray-100 text-gray-700'
                          }`}>
                            {CLIENT_TYPE_CONFIG[client.client_type]?.label || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p className="text-[#111111] flex items-center gap-1">
                              <Phone className="w-3 h-3 text-[#64748B]" /> {client.mobile}
                            </p>
                            {client.email && (
                              <p className="text-[#64748B] flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {client.email}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            STATUS_CONFIG[client.status]?.badge || 'bg-gray-100 text-gray-700'
                          }`}>
                            {STATUS_CONFIG[client.status]?.label || client.status || 'Active'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {client.client_type === 'subscription' ? (
                            <div className="text-sm">
                              <p className="text-[#0D9488] font-medium">
                                {client.quota?.bookings_remaining ?? client.bookings_per_week}/week
                              </p>
                              <p className="text-[#64748B]">${client.weekly_cost}/week</p>
                            </div>
                          ) : (
                            <p className="text-sm text-[#64748B]">
                              {client.total_bookings || 0} bookings
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openClientDetail(client)}
                              className="text-[#0D9488] hover:text-[#0D9488] hover:bg-[#F0FDFA]"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {client.client_type === 'subscription' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openGiftModal(client)}
                                className="text-[#64748B] hover:text-amber-600"
                                title="Gift Bookings"
                              >
                                <Gift className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(client)}
                              className="text-[#64748B] hover:text-[#EF4444]"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        // Registrations Tab
        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#64748B]" />
              Registration Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {registrations.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-[#94A3B8] mb-4" />
                <p className="text-[#64748B]">No registration requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {registrations.map((reg) => (
                  <div key={reg.id} className="border border-[#E2E8F0] rounded-lg p-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-sm font-bold text-[#111111]">{reg.reference}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            reg.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            reg.status === 'approved' ? 'bg-green-100 text-green-700' :
                            reg.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {reg.status}
                          </span>
                          {reg.is_migration && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                              Migration
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-[#111111]">{reg.company?.business_name}</p>
                        <p className="text-sm text-[#64748B]">
                          {reg.key_contact?.first_name} {reg.key_contact?.last_name}
                        </p>
                        <p className="text-sm text-[#64748B]">
                          {reg.key_contact?.mobile} • {reg.key_contact?.email}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {reg.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => updateRegistrationStatus(reg.id, 'approved')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateRegistrationStatus(reg.id, 'rejected')}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {reg.status === 'approved' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => {
                                setFormData({
                                  name: `${reg.key_contact?.first_name} ${reg.key_contact?.last_name}`,
                                  business_name: reg.company?.business_name,
                                  mobile: reg.key_contact?.mobile,
                                  email: reg.key_contact?.email,
                                  abn: reg.company?.abn || '',
                                  client_type: 'subscription',
                                  bookings_per_week: 5,
                                  weekly_cost: 45,
                                  status: 'active',
                                  notes: `Registered via website (${reg.reference})`
                                });
                                setShowAddModal(true);
                              }}
                              className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Create Client
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openMergeModal(reg)}
                              className="border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Merge
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewClientTerms(reg)}
                        >
                          <FileText className="w-3 h-3 mr-1" /> Terms
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add New Client</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddClient} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Client Type</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, client_type: 'subscription' })}
                      className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                        formData.client_type === 'subscription'
                          ? 'border-[#0D9488] bg-[#F0FDFA]'
                          : 'border-[#E2E8F0]'
                      }`}
                    >
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${
                        formData.client_type === 'subscription' ? 'text-[#0D9488]' : 'text-[#64748B]'
                      }`} />
                      <p className="text-sm font-medium">Subscription</p>
                      <p className="text-xs text-[#64748B]">Weekly quota</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, client_type: 'one_time' })}
                      className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                        formData.client_type === 'one_time'
                          ? 'border-[#64748B] bg-[#F8FAFC]'
                          : 'border-[#E2E8F0]'
                      }`}
                    >
                      <Package className={`w-5 h-5 mx-auto mb-1 ${
                        formData.client_type === 'one_time' ? 'text-[#64748B]' : 'text-[#94A3B8]'
                      }`} />
                      <p className="text-sm font-medium">One-Time</p>
                      <p className="text-xs text-[#64748B]">Pay per booking</p>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Business Name</label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Company Pty Ltd"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Mobile *</label>
                    <Input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="04XX XXX XXX"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Email</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">ABN</label>
                  <Input
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                    placeholder="XX XXX XXX XXX"
                  />
                </div>

                {formData.client_type === 'subscription' && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-[#F0FDFA] rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1">Weekly Quota</label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.bookings_per_week}
                        onChange={(e) => setFormData({ ...formData, bookings_per_week: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1">Weekly Cost ($)</label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.weekly_cost}
                        onChange={(e) => setFormData({ ...formData, weekly_cost: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm min-h-[60px]"
                    placeholder="Internal notes..."
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D9488] hover:bg-[#0F766E]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Client
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gift Bookings Modal */}
      {showGiftModal && giftingClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-500" />
                Gift Bookings
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowGiftModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#64748B]">
                Gift additional bookings to <strong>{giftingClient.name || giftingClient.business_name}</strong>
              </p>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Number of Bookings</label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={giftAmount}
                  onChange={(e) => setGiftAmount(parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Reason (optional)</label>
                <Input
                  value={giftReason}
                  onChange={(e) => setGiftReason(e.target.value)}
                  placeholder="e.g., Customer appreciation"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowGiftModal(false)} className="flex-1" disabled={isGifting}>
                  Cancel
                </Button>
                <Button onClick={handleGiftBookings} className="flex-1 bg-amber-500 hover:bg-amber-600" disabled={isGifting}>
                  {isGifting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                  Gift {giftAmount} Booking{giftAmount > 1 ? 's' : ''}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Terms Modal */}
      {showTermsModal && viewingTermsClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#7C3AED]" />
                Legal Agreement
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowTermsModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {viewingTermsClient.legal_agreements ? (
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>Agreed on:</strong>{' '}
                      {new Date(viewingTermsClient.legal_agreements.agreed_at).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' })}
                    </p>
                    {viewingTermsClient.legal_agreements.version && (
                      <p className="text-sm text-purple-600 mt-1">
                        Version: {viewingTermsClient.legal_agreements.version}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    {viewingTermsClient.legal_agreements.general_terms && (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>General Terms & Conditions</span>
                      </div>
                    )}
                    {viewingTermsClient.legal_agreements.service_terms && (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>Service Terms</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-[#64748B] text-center py-4">No legal agreement on record</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && mergingRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-[#7C3AED]" />
                Merge with Existing Client
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowMergeModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-800">
                  <strong>Registration:</strong> {mergingRegistration.reference}
                </p>
                <p className="text-sm text-purple-800">
                  <strong>Business:</strong> {mergingRegistration.company?.business_name}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium">This will:</p>
                <ul className="text-sm text-amber-800 list-disc pl-5 mt-1 space-y-1">
                  <li>Update client's contact details, company name, ABN</li>
                  <li>Record their legal agreement acceptance</li>
                  <li><strong>Preserve</strong> their weekly quota and cost</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Existing Client Mobile Number
                </label>
                <Input
                  value={mergeTargetMobile}
                  onChange={(e) => setMergeTargetMobile(e.target.value)}
                  placeholder="04XX XXX XXX"
                />
              </div>

              {mergeError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {mergeError}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowMergeModal(false)} disabled={isMerging}>
                  Cancel
                </Button>
                <Button
                  onClick={handleMergeRegistration}
                  disabled={isMerging || !mergeTargetMobile}
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                >
                  {isMerging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Merge Registration
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Set Password Modal */}
      {showPasswordModal && passwordClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-[#0D9488]" />
                Set Portal Password
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowPasswordModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-[#F0FDFA] border border-[#0D9488]/20 rounded-lg p-3">
                  <p className="text-sm text-[#0F766E]">
                    Setting password for: <span className="font-semibold">{passwordClient.name || passwordClient.mobile}</span>
                  </p>
                  <p className="text-xs text-[#64748B] mt-1">
                    Mobile: {passwordClient.mobile}
                  </p>
                </div>
                
                {passwordError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {passwordError}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">New Password *</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter password (min 4 characters)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Confirm Password *</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPasswordModal(false)}
                    disabled={isSettingPassword}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSetPassword}
                    disabled={isSettingPassword || !newPassword || !confirmPassword}
                    className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                  >
                    {isSettingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                    Set Password
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && addingUserToClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Authorized User</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAddUserModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Name *</label>
                  <Input
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Mobile *</label>
                  <Input
                    type="tel"
                    value={newUserData.mobile}
                    onChange={(e) => setNewUserData({ ...newUserData, mobile: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Email</label>
                  <Input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Role</label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md"
                  >
                    <option value="installer">Installer</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddUserModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={isAddingUser}>
                    {isAddingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Add User
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Detail Drawer */}
      {selectedClient && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-start justify-end">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl animate-in slide-in-from-right">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-[#111111]">
                {isEditMode ? 'Edit Client' : 'Client Details'}
              </h2>
              <div className="flex items-center gap-2">
                {!isEditMode ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#0D9488] text-[#0D9488] hover:bg-[#F0FDFA]"
                    onClick={startEditMode}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={cancelEditMode} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button size="sm" className="bg-[#0D9488] hover:bg-[#0F766E]" onClick={saveClientEdit} disabled={isSaving}>
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                    </Button>
                  </>
                )}
                <button onClick={closeClientDetail} className="text-[#94A3B8] hover:text-[#111111] ml-2">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Client Reference and Type */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-lg font-mono font-bold text-[#111111]">
                  {selectedClient.client_ref || `${selectedClient.client_type === 'subscription' ? 'SUB' : 'CLT'}-${(selectedClient.id || '').slice(0, 8).toUpperCase()}`}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  CLIENT_TYPE_CONFIG[selectedClient.client_type]?.badge || 'bg-gray-100 text-gray-700'
                }`}>
                  {CLIENT_TYPE_CONFIG[selectedClient.client_type]?.label || 'Client'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  STATUS_CONFIG[selectedClient.status]?.badge || 'bg-green-100 text-green-700'
                }`}>
                  {STATUS_CONFIG[selectedClient.status]?.label || 'Active'}
                </span>
                {selectedClient.legal_agreements && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Terms Accepted
                  </span>
                )}
              </div>

              {/* Key Contact */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-[#64748B] text-sm mb-3 flex items-center gap-2 font-medium">
                  <User className="w-4 h-4" /> Key Contact
                </p>
                {isEditMode ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#64748B] mb-1 block">Name</label>
                      <Input
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#64748B] mb-1 block">Mobile</label>
                      <Input
                        value={editForm.mobile || ''}
                        onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-[#64748B] mb-1 block">Email</label>
                      <Input
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[#111111] font-semibold text-lg">{selectedClient.name}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-[#475569]">
                        <Phone className="w-4 h-4 text-[#0D9488]" />
                        {selectedClient.mobile}
                      </span>
                      {selectedClient.email && (
                        <span className="flex items-center gap-1 text-[#475569]">
                          <Mail className="w-4 h-4 text-[#0D9488]" />
                          {selectedClient.email}
                        </span>
                      )}
                    </div>
                    {/* Set Password Button - only for subscription clients */}
                    {selectedClient.client_type === 'subscription' && (
                      <button
                        onClick={() => openPasswordModal(selectedClient)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-[#0D9488] hover:text-[#0F766E] font-medium"
                      >
                        <Key className="w-3.5 h-3.5" />
                        Set Portal Password
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Company Details */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-[#64748B] text-sm mb-3 flex items-center gap-2 font-medium">
                  <Building2 className="w-4 h-4" /> Company Details
                </p>
                {isEditMode ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="text-xs text-[#64748B] mb-1 block">Business Name</label>
                      <Input
                        value={editForm.business_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-[#64748B] mb-1 block">ABN</label>
                      <Input
                        value={editForm.abn || ''}
                        onChange={(e) => setEditForm({ ...editForm, abn: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[#111111] font-semibold">{selectedClient.business_name || '-'}</p>
                    {selectedClient.abn && (
                      <p className="text-sm text-[#64748B] flex items-center gap-1">
                        <Hash className="w-3 h-3" /> ABN: {selectedClient.abn}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Subscription Details (only for subscription clients) */}
              {selectedClient.client_type === 'subscription' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-[#F0FDFA] to-white rounded-lg p-4 border border-[#0D9488]/20">
                    <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" /> Weekly Quota
                    </p>
                    {isEditMode ? (
                      <Input
                        type="number"
                        min="1"
                        value={editForm.bookings_per_week || 5}
                        onChange={(e) => setEditForm({ ...editForm, bookings_per_week: parseInt(e.target.value) })}
                        className="text-sm"
                      />
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-[#0D9488]">
                          {selectedClient.quota?.effective_limit || selectedClient.bookings_per_week}
                          <span className="text-sm font-normal text-[#64748B] ml-1">/ week</span>
                        </p>
                        {selectedClient.quota?.bonus_bookings > 0 && (
                          <p className="text-xs text-green-600">+{selectedClient.quota.bonus_bookings} bonus</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-[#FEF3C7] to-white rounded-lg p-4 border border-[#D97706]/20">
                    <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Weekly Cost
                    </p>
                    {isEditMode ? (
                      <Input
                        type="number"
                        min="0"
                        value={editForm.weekly_cost || 45}
                        onChange={(e) => setEditForm({ ...editForm, weekly_cost: parseFloat(e.target.value) })}
                        className="text-sm"
                      />
                    ) : (
                      <p className="text-2xl font-bold text-[#D97706]">
                        ${selectedClient.weekly_cost}
                        <span className="text-sm font-normal text-[#64748B] ml-1">/ week</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Legal Agreement Info */}
              {selectedClient.legal_agreements && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <p className="text-purple-700 text-sm mb-2 flex items-center gap-2 font-medium">
                    <FileText className="w-4 h-4" /> Legal Agreement
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="text-purple-800">
                      <span className="font-medium">Agreed on:</span>{' '}
                      {new Date(selectedClient.legal_agreements.agreed_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Melbourne' })}
                    </p>
                    {selectedClient.legal_agreements.version && (
                      <p className="text-purple-600">Version: {selectedClient.legal_agreements.version}</p>
                    )}
                    <div className="pt-2">
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-purple-700 hover:text-purple-900 hover:underline font-medium"
                        data-testid="view-legal-document-btn"
                      >
                        <Eye className="w-4 h-4" /> View Full Document
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Authorized Users (only for subscription clients) */}
              {selectedClient.client_type === 'subscription' && (
                <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <div className="bg-[#F8FAFC] p-3 flex items-center justify-between border-b border-[#E2E8F0]">
                    <p className="text-[#111111] font-medium flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      Authorized Users ({selectedClient.authorized_users?.length || 0})
                    </p>
                    <Button
                      size="sm"
                      onClick={() => openAddUserModal(selectedClient)}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs"
                    >
                      <UserPlus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="p-3">
                    {(!selectedClient.authorized_users || selectedClient.authorized_users.length === 0) ? (
                      <p className="text-center text-[#94A3B8] text-sm py-4">No authorized users yet</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedClient.authorized_users.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#111111]">{user.name}</p>
                                <p className="text-xs text-[#64748B]">{user.mobile}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'supervisor' ? 'bg-purple-100 text-purple-700' :
                              user.role === 'admin' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role || 'installer'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-[#64748B] text-sm mb-2 font-medium">Notes</p>
                {isEditMode ? (
                  <textarea
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm min-h-[80px]"
                  />
                ) : (
                  <p className="text-sm text-[#475569]">{selectedClient.notes || 'No notes'}</p>
                )}
              </div>

              {/* Activity History */}
              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                <div className="bg-[#F8FAFC] p-3 border-b border-[#E2E8F0]">
                  <p className="text-[#111111] font-medium flex items-center gap-2">
                    <History className="w-4 h-4 text-[#64748B]" />
                    Activity History
                  </p>
                </div>
                <div className="p-3 max-h-[300px] overflow-y-auto">
                  {loadingActivity ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#0D9488]" />
                    </div>
                  ) : clientActivityHistory.length === 0 ? (
                    <p className="text-center text-[#94A3B8] text-sm py-8">No activity recorded yet</p>
                  ) : (
                    <div className="space-y-3">
                      {clientActivityHistory.map((activity, index) => (
                        <div key={index} className="flex gap-3 text-sm border-l-2 border-[#E2E8F0] pl-3 py-1">
                          <div className="flex-1">
                            <p className="text-[#111111]">{activity.description}</p>
                            <p className="text-xs text-[#94A3B8] mt-1">
                              {formatActivityDate(activity.created_at)}
                            </p>
                          </div>
                          <span className={`h-fit px-2 py-0.5 rounded text-xs font-medium ${
                            activity.event_type?.includes('booking') ? 'bg-blue-100 text-blue-700' :
                            activity.event_type?.includes('otp') ? 'bg-amber-100 text-amber-700' :
                            activity.event_type?.includes('payment') ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {activity.event_type?.replace(/_/g, ' ') || 'activity'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Meta Info */}
              <div className="text-xs text-[#94A3B8] flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
                <span>Created: {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Melbourne' }) : 'Unknown'}</span>
                <span>ID: {selectedClient.id}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
